/**
 * Contact Edit Dialog — for address and emergency contact types.
 * Opens as a centered modal with backdrop blur.
 * Edits are applied to the parent's state on confirm (not persisted to API directly).
 */

import { useState, useEffect } from 'react';
import { X, MapPin, Shield } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { validateContactForm } from '@/lib/contact-validation';
import { COUNTRIES } from '@/lib/countries';
import { cn } from '@/lib/utils';
import type { Contact } from '@/lib/mock-data';

type EditableContact = Contact & { _isNew?: boolean; _deleted?: boolean };

const ADDRESS_TYPE_IDS = new Set([6, 13, 17, 18]);

const ADDRESS_TYPE_NAMES: Record<number, string> = {
  6: 'Study Address', 13: 'Work Address', 17: 'Home Address', 18: 'Postal Address',
};

interface ContactEditDialogProps {
  contact: EditableContact;
  onConfirm: (updated: EditableContact) => void;
  onCancel: () => void;
}

export function ContactEditDialog({ contact, onConfirm, onCancel }: ContactEditDialogProps) {
  const isAddress = ADDRESS_TYPE_IDS.has(contact.typeId);
  const isEmergency = contact.typeId === 12;
  const isNew = !!contact._isNew;

  // Address fields
  const [country, setCountry] = useState(contact.contactValue.country || '');
  const [building, setBuilding] = useState(contact.contactValue.building || '');
  const [unit, setUnit] = useState(contact.contactValue.unit || '');
  const [streetNumber, setStreetNumber] = useState(contact.contactValue.streetNumber || '');
  const [streetName, setStreetName] = useState(contact.contactValue.streetName || '');
  const [postalDelivery, setPostalDelivery] = useState(contact.contactValue.postalDeliveryInfo || '');
  const [locality, setLocality] = useState(contact.contactValue.locality || '');
  const [region, setRegion] = useState(contact.contactValue.region || '');
  const [postcode, setPostcode] = useState(contact.contactValue.postcode || '');

  // Emergency contact fields
  const existingVal = contact.contactValue.val || '';
  const parts = isEmergency ? existingVal.split(' | ') : [];
  const [emergencyName, setEmergencyName] = useState(parts[0] || '');
  const [emergencyNumber, setEmergencyNumber] = useState(parts.slice(1).join(' | ') || '');

  // Address type selector (only for new contacts)
  const [typeId, setTypeId] = useState(contact.typeId);

  const [errors, setErrors] = useState<string[]>([]);

  // Reset state when contact changes
  useEffect(() => {
    const cv = contact.contactValue;
    setCountry(cv.country || '');
    setBuilding(cv.building || '');
    setUnit(cv.unit || '');
    setStreetNumber(cv.streetNumber || '');
    setStreetName(cv.streetName || '');
    setPostalDelivery(cv.postalDeliveryInfo || '');
    setLocality(cv.locality || '');
    setRegion(cv.region || '');
    setPostcode(cv.postcode || '');
    setTypeId(contact.typeId);
    const val = cv.val || '';
    if (contact.typeId === 12) {
      const p = val.split(' | ');
      setEmergencyName(p[0] || '');
      setEmergencyNumber(p.slice(1).join(' | ') || '');
    }
    setErrors([]);
  }, [contact]);

  const handleConfirm = () => {
    let updated: EditableContact;

    if (ADDRESS_TYPE_IDS.has(typeId)) {
      updated = {
        ...contact,
        typeId,
        contactValue: {
          ...contact.contactValue,
          country, building, unit, streetNumber, streetName,
          postalDeliveryInfo: postalDelivery,
          locality, region, postcode,
          val: undefined,
        },
      };
    } else {
      // Emergency contact
      const combined = emergencyNumber ? `${emergencyName} | ${emergencyNumber}` : emergencyName;
      updated = {
        ...contact,
        typeId,
        contactValue: {
          ...contact.contactValue,
          val: combined,
          formattedAddress: undefined,
        },
      };
    }

    // Validate
    const validationErrors = validateContactForm({
      typeId: updated.typeId,
      val: updated.contactValue.val || '',
      building: updated.contactValue.building || '',
      unit: updated.contactValue.unit || '',
      streetNumber: updated.contactValue.streetNumber || '',
      streetName: updated.contactValue.streetName || '',
      locality: updated.contactValue.locality || '',
      region: updated.contactValue.region || '',
      postcode: updated.contactValue.postcode || '',
      country: updated.contactValue.country || '',
    });

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    onConfirm(updated);
  };

  const title = isNew
    ? (isAddress ? 'Add Address' : 'Add Emergency Contact')
    : (isAddress ? `Edit ${ADDRESS_TYPE_NAMES[typeId] || 'Address'}` : 'Edit Emergency Contact');

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 animate-fade-in" onClick={onCancel} aria-hidden="true" />
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col animate-scale-in">
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-10 h-10 rounded-xl flex items-center justify-center',
                isAddress ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
              )}>
                {isAddress ? <MapPin className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
              </div>
              <div>
                <h2 className="text-lg font-extrabold text-foreground tracking-tight">{title}</h2>
                {isNew && isAddress && (
                  <select
                    value={typeId}
                    onChange={(e) => setTypeId(Number(e.target.value))}
                    className="mt-1 text-xs bg-surface-low border-none rounded-lg py-1 pl-2 pr-6 focus:ring-2 focus:ring-primary/20 cursor-pointer"
                    aria-label="Address type"
                  >
                    {Object.entries(ADDRESS_TYPE_NAMES).map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <button onClick={onCancel} className="p-2 hover:bg-surface-low rounded-full transition-colors" aria-label="Close">
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-5">
            {isAddress && (
              <>
                <div>
                  <Label htmlFor="dialog-country">Country</Label>
                  <select
                    id="dialog-country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="mt-1 flex h-10 w-full rounded-xl bg-surface-low px-4 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 border-none cursor-pointer"
                  >
                    <option value="">Select country</option>
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="dialog-building">Building</Label>
                    <Input id="dialog-building" value={building} onChange={(e) => setBuilding(e.target.value)} placeholder="Building name" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="dialog-unit">Unit</Label>
                    <Input id="dialog-unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Unit / Apt" className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-4">
                    <Label htmlFor="dialog-street-no">Street No.</Label>
                    <Input id="dialog-street-no" value={streetNumber} onChange={(e) => setStreetNumber(e.target.value)} placeholder="128" className="mt-1" />
                  </div>
                  <div className="col-span-8">
                    <Label htmlFor="dialog-street">Street</Label>
                    <Input id="dialog-street" value={streetName} onChange={(e) => setStreetName(e.target.value)} placeholder="Founders Avenue" className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="dialog-postal">Postal Delivery Location</Label>
                  <Input id="dialog-postal" value={postalDelivery} onChange={(e) => setPostalDelivery(e.target.value)} placeholder="PO Box, Locked Bag, etc." className="mt-1" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="dialog-locality">Locality</Label>
                    <Input id="dialog-locality" value={locality} onChange={(e) => setLocality(e.target.value)} placeholder="City / Suburb" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="dialog-region">Region</Label>
                    <Input id="dialog-region" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="State" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="dialog-postcode">Postcode</Label>
                    <Input id="dialog-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="2000" className="mt-1" />
                  </div>
                </div>
              </>
            )}

            {isEmergency && (
              <>
                <div>
                  <Label htmlFor="dialog-emerg-name">Contact Name</Label>
                  <Input id="dialog-emerg-name" value={emergencyName} onChange={(e) => setEmergencyName(e.target.value)} placeholder="Jane Smith" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="dialog-emerg-number">Contact Number</Label>
                  <Input id="dialog-emerg-number" value={emergencyNumber} onChange={(e) => setEmergencyNumber(e.target.value)} placeholder="+61412345678" type="tel" className="mt-1" />
                </div>
              </>
            )}

            {errors.length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl space-y-1" role="alert">
                {errors.map((err, i) => (
                  <p key={i} className="text-sm text-red-700">{err}</p>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 pt-4 border-t border-surface-low flex justify-end gap-3">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button onClick={handleConfirm}>{isNew ? 'Add' : 'Update'}</Button>
          </div>
        </div>
      </div>
    </>
  );
}
